import type { ClassConstructorInterface } from '@zaoblako/shared/common/interfaces/class-constructor.interface';

import { isEmpty } from 'lodash';

const ELLIPSIS_DOTS_LENGTH = 3;

const ELLIPSIS_DOTS = '.'.repeat(ELLIPSIS_DOTS_LENGTH);

const FLOATING_POINT_ROUNDING_EPSILON = 1e-9;

const ONES_WORDS_MASCULINE = [
  '', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять',
];
const ONES_WORDS_FEMININE = [
  '', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять',
];
const NUMBERS_FROM_TEN_TO_NINETEEN_WORDS = [
  'десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать',
  'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать',
];
const TENS_MULTIPLE_OF_TEN_WORDS = [
  '', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят',
  'девяносто',
];
const HUNDREDS_WORDS = [
  '', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот',
  'девятьсот',
];

type MagnitudeGroupDeclensionForms = {
  feminine: boolean;
  wordFormOne: string;
  wordFormFew: string;
  wordFormMany: string;
};

const MAGNITUDE_GROUP_DECLENSION_FORMS: MagnitudeGroupDeclensionForms[] = [
  { feminine: false, wordFormOne: '', wordFormFew: '', wordFormMany: '' },
  { feminine: true, wordFormOne: 'тысяча', wordFormFew: 'тысячи', wordFormMany: 'тысяч' },
  { feminine: false, wordFormOne: 'миллион', wordFormFew: 'миллиона', wordFormMany: 'миллионов' },
  { feminine: false, wordFormOne: 'миллиард', wordFormFew: 'миллиарда', wordFormMany: 'миллиардов' },
];

const pluralizeRussian = (
  count: number,
  wordFormOne: string,
  wordFormFew: string,
  wordFormMany: string,
): string => {
  const remainderWhenDividedByTen = count % 10;
  const remainderWhenDividedByOneHundred = count % 100;
  if (remainderWhenDividedByOneHundred >= 11 && remainderWhenDividedByOneHundred <= 14) {
    return wordFormMany;
  }
  if (remainderWhenDividedByTen === 1) {
    return wordFormOne;
  }
  if (remainderWhenDividedByTen >= 2 && remainderWhenDividedByTen <= 4) {
    return wordFormFew;
  }
  return wordFormMany;
};

const rublesDeclensionRussian = (count: number): string =>
  pluralizeRussian(count, 'рубль', 'рубля', 'рублей');

const kopeksDeclensionRussian = (count: number): string =>
  pluralizeRussian(count, 'копейка', 'копейки', 'копеек');

const convertThreeDigitChunkToWordsRussian = (
  threeDigitChunk: number,
  useFeminineOnesForms: boolean,
): string => {
  const hundredsDigit = Math.floor(threeDigitChunk / 100);
  const remainderBelowOneHundred = threeDigitChunk % 100;
  const wordFragments: string[] = [];

  if (hundredsDigit) {
    wordFragments.push(HUNDREDS_WORDS[hundredsDigit]);
  }
  if (remainderBelowOneHundred < 10) {
    if (remainderBelowOneHundred) {
      wordFragments.push(
        useFeminineOnesForms
          ? ONES_WORDS_FEMININE[remainderBelowOneHundred]
          : ONES_WORDS_MASCULINE[remainderBelowOneHundred],
      );
    }
  } else if (remainderBelowOneHundred < 20) {
    wordFragments.push(NUMBERS_FROM_TEN_TO_NINETEEN_WORDS[remainderBelowOneHundred - 10]);
  } else {
    const tensPlaceIndex = Math.floor(remainderBelowOneHundred / 10);
    const onesDigit = remainderBelowOneHundred % 10;
    wordFragments.push(TENS_MULTIPLE_OF_TEN_WORDS[tensPlaceIndex]);
    if (onesDigit) {
      wordFragments.push(
        useFeminineOnesForms ? ONES_WORDS_FEMININE[onesDigit] : ONES_WORDS_MASCULINE[onesDigit],
      );
    }
  }
  return wordFragments.join(' ').trim();
};

/** Integer 0 … 999 999 999 999 (words, no currency). */
const convertIntegerToWordsRussian = (integer: number): string => {
  if (integer === 0) {
    return 'ноль';
  }

  const wordFragments: string[] = [];
  let remainingInteger = integer;
  let magnitudeGroupIndex = 0;

  while (remainingInteger > 0) {
    const threeDigitGroup = remainingInteger % 1000;
    remainingInteger = Math.floor(remainingInteger / 1000);
    if (threeDigitGroup) {
      const declensionFormsForMagnitude =
        MAGNITUDE_GROUP_DECLENSION_FORMS[magnitudeGroupIndex]
        ?? MAGNITUDE_GROUP_DECLENSION_FORMS[MAGNITUDE_GROUP_DECLENSION_FORMS.length - 1];
      const chunkWords = convertThreeDigitChunkToWordsRussian(
        threeDigitGroup,
        declensionFormsForMagnitude.feminine,
      );
      if (magnitudeGroupIndex === 0) {
        wordFragments.unshift(chunkWords);
      } else {
        const magnitudeUnitWord = pluralizeRussian(
          threeDigitGroup,
          declensionFormsForMagnitude.wordFormOne,
          declensionFormsForMagnitude.wordFormFew,
          declensionFormsForMagnitude.wordFormMany,
        );
        wordFragments.unshift(`${chunkWords} ${magnitudeUnitWord}`);
      }
    }
    magnitudeGroupIndex += 1;
  }

  return wordFragments.join(' ').trim();
};

/** Non-negative integer digits grouped by thousands with a regular space (e.g. `1 234`). */
const formatIntegerWithSpaceThousands = (nonNegativeInteger: number): string => {
  const digits = String(nonNegativeInteger);
  const groups: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    groups.unshift(digits.slice(Math.max(0, end - 3), end));
  }
  return groups.join(' ');
};

export class Utils {
  public static readonly ELLIPSIS_DOTS_LENGTH = ELLIPSIS_DOTS_LENGTH;

  public static readonly ELLIPSIS_DOTS = ELLIPSIS_DOTS;

  public static sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

  /**
   * Get the number of digits in an integer
   *
   * @param value Integer value
   *
   * @returns Number of digits
   */
  public static getIntegerDigitsCount = (value: number): number => {
    const absValue = Math.abs(value ?? 0);
    if (absValue === 0) {
      return 1;
    }
    return Math.floor(Math.log10(absValue)) + 1;
  };

  /**
   * Check whether received array-like value is empty
   *
   * @deprecated Use `isEmpty` function from `lodash` package instead
   *
   * @param value to check (any array-like value: array, string, etc.)
   *
   * @returns Check result (`boolean` value)
   */
  public static isEmpty = (value: ArrayLike<any>): boolean => isEmpty(value);

  /**
   * Check whether received value is `null` or `undefined`
   * but not the other `falsy` value
   *
   * @param value Value to check
   *
   * @returns Check result (`boolean` value)
   */
  public static isNil = (value?: any): value is null | undefined => {
    return value === null || value === undefined;
  };

  /**
   * Check whether received value is not `null` or `undefined` value
   * but can be the other `falsy` value
   *
   * @param value Value to check
   *
   * @returns Check result (`boolean` value)
   */
  public static isNotNil = (value?: any): boolean => !Utils.isNil(value);

  /**
   * Like `String.prototype.toUpperCase()` but only for the first letter.
   *
   * @param value
   */
  public static upperFirst = (value: string): string => {
    if (!value) {
      return '';
    }

    const [first, ...rest] = value.split('');
    return [first.toUpperCase(), ...rest].join('');
  };

  /**
   * Integer part of a number with groups of three digits separated by a regular space (e.g. `1 234`).
   * For non-negative integers only.
   */
  public static formatIntegerWithSpaceThousandsRu = (nonNegativeInteger: number): string =>
    formatIntegerWithSpaceThousands(nonNegativeInteger);

  /**
   * Format a numeric template value: thousands separated by spaces, decimal comma and two fractional digits when needed.
   */
  public static formatNumericValueForRuTemplate = (value: number): string => {
    if (!Number.isFinite(value)) {
      return '';
    }
    const sign = value < 0 ? '-' : '';
    const absoluteValue = Math.abs(value);
    const intPart = Math.floor(absoluteValue + FLOATING_POINT_ROUNDING_EPSILON);
    let fractionalHundredths = Math.round(
      (absoluteValue - intPart) * 100 + FLOATING_POINT_ROUNDING_EPSILON,
    );
    let integerOut = intPart;
    if (fractionalHundredths >= 100) {
      integerOut += 1;
      fractionalHundredths = 0;
    }
    const intStr = formatIntegerWithSpaceThousands(integerOut);
    if (fractionalHundredths === 0) {
      return `${sign}${intStr}`;
    }
    return `${sign}${intStr},${String(fractionalHundredths).padStart(2, '0')}`;
  };

  /**
   * Convert monetary amount to Russian words in parentheses, ruble and kopek words with declension;
   * kopeks as two digits. No guillemets, no digit prefix (use {@link Utils.formatNumericValueForRuTemplate} for digits).
   *
   * @param amount Value in rubles (fractional part — kopecks, two decimals)
   *
   * @returns Phrase like `(Одна тысяча двести тридцать четыре) рубля 56 копеек`; negative amounts use the `минус ` prefix.
   */
  public static amountInWordsRu = (amount: number): string => {
    if (!Number.isFinite(amount)) {
      return '';
    }

    const negativePrefix = amount < 0 ? 'минус ' : '';
    const absoluteAmount = Math.abs(amount);
    const wholeRubles = Math.floor(absoluteAmount + FLOATING_POINT_ROUNDING_EPSILON);
    let fractionalKopeks = Math.round(
      (absoluteAmount - wholeRubles) * 100 + FLOATING_POINT_ROUNDING_EPSILON,
    );
    if (fractionalKopeks >= 100) {
      fractionalKopeks = 0;
    }

    const wordsInParens = Utils.upperFirst(convertIntegerToWordsRussian(wholeRubles));
    const rublesWord = rublesDeclensionRussian(wholeRubles);
    const kopeksDigits = String(fractionalKopeks).padStart(2, '0');
    const kopeksWord = kopeksDeclensionRussian(fractionalKopeks);

    return `${negativePrefix}(${wordsInParens}) ${rublesWord} ${kopeksDigits} ${kopeksWord}`;
  };

  /**
   * Get first element of list
   *
   * @param [list] List (array of array-like value)
   *
   * @returns First element of `undefined` if list is empty or not defined
   */
  public static first = <T = any>(list?: ArrayLike<T>): T =>
    list ? list[0] : undefined;

  /**
   * Get last element of list
   *
   * @param list List (array of array-like value)
   *
   * @returns Last element of `undefined` if list is empty or not defined
   */
  public static last = <T = any>(list?: ArrayLike<T>): T =>
    list ? list[list.length - 1] : undefined;

  /**
   * Returns the total value of all elements of an array of numbers.
   * If received array empty or not defined then method returns zero.
   *
   * @param list Array of numbers to sum
   *
   * @returns Total value of all elements
   */
  public static sum = (list?: number[]): number => {
    if (Utils.isNil(list) || Utils.isEmpty(list)) {
      return 0;
    }

    return list.reduce((first, second) => first + second, 0);
  };

  /**
   * Split array-like value to chunks with fixed size (last chunk may be shorter).
   *
   * @param list List to split. Array-like value (array, string etc.)
   * @param size Size of chunk
   */
  public static chunk<T extends ArrayLike<T> = ArrayLike<string>>(list: T, size: number): T[] {
    if (size < 1 || list.length <= size) {
      return [list];
    }

    const chunksCount = Math.ceil(list.length / size);
    const result: T[] = [];
    for (let i = 0; i < chunksCount; i++) {
      result.push(
        typeof list === 'string'
          ? (<string>list).slice(i * size, i * size + size)
          : Array.prototype.slice.call(list, i * size, i * size + size),
      );
    }
    return result;
  }

  /** Formatted output of the float value */
  public static formatFloat = (value: number, precision?: number): string => {
    const sign = value < 0 ? '-' : '';
    const [integer, fractional] = Math.abs(value).toFixed(precision ?? 2).split('.');

    const digits = integer.split('').reverse() as ArrayLike<string>;
    const chunks = Utils.chunk(digits, 3);

    const formatted = chunks
      .map(chunk => (<string[]>chunk).join(''))
      .join(' ')
      .split('')
      .reverse()
      .join('');

    return fractional
      ? `${sign}${formatted}.${fractional}`
      : `${sign}${formatted}`;
  };

  /**
   * Formatted output of the integer value (`9 999 999...`);
   *
   * @param value Value to format
   *
   * @returns Formatted value
   */
  public static formatInteger = (value: number): string => {
    const digits = String(Math.round(value))
      .split('')
      .reverse() as ArrayLike<string>;
    const chunks = Utils.chunk(digits, 3);
    return chunks
      .map(chunk => (<string[]>chunk).join(''))
      .join(' ')
      .split('')
      .reverse()
      .join('');
  };

  public static getStackTrace = (): string[] => {
    const err = new Error();
    const stack = (err.stack || '').split('\n');
    return stack
      .slice(2)
      .map(chunk => chunk.slice(7));
  };

  public static substringAndEllipsis = (str: string, length = 200): string => {
    return str.length > length ? str.substring(0, length) + Utils.ELLIPSIS_DOTS : str;
  };

  /** Обрезает хвост с CSS/шаблонной разметкой, если она попала в plain text после снятия тегов. */
  private static stripCssLikeSuffix = (text: string): string => {
    const cssStartPattern = /(@media\b|@import\b|\.[a-zA-Z_][\w-]*\s*\{|#[a-zA-Z_][\w-]*\s*\{|:[a-z-]+\s*\{)/i;
    const match = cssStartPattern.exec(text);
    if (match?.index === undefined) {
      return text;
    }
    return text.slice(0, match.index).trim();
  };

  /**
   * Converts markup to text
   * @param text
   * @param keepNewLines
   */
  public static convertHTMLToText = (text: string, keepNewLines = false): string => {
    if (!text) {
      return '';
    }
    let source = keepNewLines ? text.replace(/<br.?\/?>/gm, '\n') : text;
    source = source
      .replace(/<!--[\s\S]*?-->/gi, '')
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, '')
      .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, '')
      .replace(/<svg\b[^>]*>[\s\S]*?<\/svg>/gi, '')
      .replace(/<blockquote[^>]*>[\s\S]*?<\/blockquote>/gi, '')
      .replace(/<\/p>/gi, '\n')
      .replace(/<li>/gi, '    ')
      .replace(/<\/li>/gi, '\n')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/(<([^>]+)>)/gi, '');
    source = Utils.stripCssLikeSuffix(source);
    return source.trim();
  };

  /**
   * Found percentage for value
   *
   * @param value Value to calculate
   * @param total Total value as percentage reference
   *
   * @returns Value in percents
   */
  public static percentOf = (value: number, total: number): number => total !== 0
    ? (value / total) * 100
    : 0;

  /**
   * Get parent classes list for received class
   *
   * @param cls Class to get ancestors
   *
   * @returns Ancestors list
   */
  public static getParentClasses = (cls: ClassConstructorInterface<any>): ClassConstructorInterface<any>[] => {
    const parentClasses: ClassConstructorInterface<any>[] = [];

    let currentPrototype = Object.getPrototypeOf(cls);

    while (currentPrototype && currentPrototype !== Function.prototype && currentPrototype !== Object.prototype) {
      if (typeof currentPrototype === 'function') {
        parentClasses.push(currentPrototype);
      }
      currentPrototype = Object.getPrototypeOf(currentPrototype);
    }

    return parentClasses;
  };

  /**
   * Get full inheritance chain for received class
   *
   * @param cls Class to get inheritance chain
   *
   * @returns Classes list where:
   *   - The first class is received class;
   *   - The rest classes is it's ancestors;
   */
  public static getInheritahceChain = (cls: ClassConstructorInterface<any>): ClassConstructorInterface<any>[] => [
    cls,
    ...Utils.getParentClasses(cls),
  ];
}
